import { ContactType } from '../contact/contact.type';
import { Entity } from '../entity';
import { Party } from '../entity.status/enum';
import { resolveUserParty } from '../entity.status/party.resolver';

/** The logged-in contact, as much of it as resolving an author needs. */
export type ItemAuthor = Entity & { type?: ContactType | string; parents?: { _id: string; type?: ContactType | string }[] };

/**
 * Who raised a line, for the Draft edit window and the office-only move (E1-05, SP-7).
 *
 * `Entity` plus the two facts it cannot carry: Office and Field are the same contact type, so the party
 * has to be stored as resolved, and a guard runs sync so it cannot look the author's company up later.
 */
export interface ItemCreator extends Entity {
    party: Party;
    companyId?: string;
}

export function buildItemCreator(author: ItemAuthor | null | undefined, surface: Party, actingCompanyId?: string | null): ItemCreator | undefined {

    if (!author?._id) return undefined;

    const party = resolveUserParty(author, surface);

    return { _id: author._id, name: author.name, party, companyId: party === Party.Contractor ? resolveContractorCompanyId(author, actingCompanyId) : undefined };
}

/** A login may belong to several companies, so the one the session acts for wins; the portal stamps and scopes lines with that company (WBS-12030). */
export function resolveContractorCompanyId(author: ItemAuthor, actingCompanyId?: string | null): string {

    if (author.type === ContactType.Subcontractor) return author._id;

    const companies = author.parents?.filter(parent => parent.type === ContactType.Subcontractor) ?? [];

    return (companies.find(company => company._id === actingCompanyId) ?? companies[0])?._id ?? author._id;
}

/**
 * Whether a caller may edit a line still in its author's window.
 *
 * A line with no author predates E1-14's backfill, so the window is open rather than shut: refusing
 * would lock every line raised before the cutover out of the status it is sitting in.
 */
export function canEditAsAuthor(creator: ItemCreator | null | undefined, party: Party, companyId?: string): boolean {

    if (!creator) return true;
    if (creator.party !== party) return false;

    return creator.party !== Party.Contractor || !creator.companyId || creator.companyId === companyId;
}

/** True when the office raised the line — the only lines that may be moved between stages. */
export function isOfficeRaised(creator: ItemCreator | null | undefined): boolean {

    return !creator || creator.party === Party.Office;
}
